import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Flame, Pencil, Eraser } from 'lucide-react';
import ChessBoardView from '@/components/chess/ChessBoardView';
import { Button } from '@/components/ui/button';

const INITIAL_FEN = '4k3/8/8/8/8/8/8/R3K2R w - - 0 1';
const FILES = 'abcdefgh';

export default function LavaFloorEditor({ open, onClose, studyId, activity, onSaved, startInPresentation = false, lavaActivities = [], onNavigateActivity }) {
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [presentationMode, setPresentationMode] = useState(false);
  const presentationBoardAreaRef = useRef(null);
  const [presentationBoardWidth, setPresentationBoardWidth] = useState(680);

  useEffect(() => {
    if (!presentationMode) return;

    const area = presentationBoardAreaRef.current;
    if (!area) return;

    const measure = () => {
      const availableWidth = area.clientWidth;
      const availableHeight = Math.max(220, window.innerHeight - 300);
      if (!availableWidth) return;

      const nextWidth = Math.max(
        220,
        Math.min(680, availableWidth, availableHeight)
      );

      setPresentationBoardWidth(current =>
        current === nextWidth ? current : nextWidth
      );
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(area);
    window.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [presentationMode]);
  const [fen, setFen] = useState(INITIAL_FEN);
  const [positionEditorOpen, setPositionEditorOpen] = useState(false);
  const [editFen, setEditFen] = useState(INITIAL_FEN);
  const [editTool, setEditTool] = useState(null);
  const [lineType, setLineType] = useState('rank');
  const [line, setLine] = useState('4');
  const [ignitedLines, setIgnitedLines] = useState([]);
  const [arrows, setArrows] = useState([]);
  const [circles, setCircles] = useState({});
  const [steps, setSteps] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [stepText, setStepText] = useState('');
  const [presentationSnapshot, setPresentationSnapshot] = useState(null);

  useEffect(() => {
    if (!open) return;

    const savedLines = Array.isArray(activity?.lava_config?.ignitedLines)
      ? activity.lava_config.ignitedLines
      : [];
    const rawSteps = Array.isArray(activity?.lava_config?.steps)
      && activity.lava_config.steps.length
      ? activity.lava_config.steps
      : [{
          title: 'Posición inicial',
          explanation: '',
          fen: activity?.fen || INITIAL_FEN,
          ignitedLines: savedLines,
          arrows: [],
          circles: {},
        }];
    // Normaliza los datos de cada escena y conserva compatibilidad con actividades antiguas.
    const savedSteps = rawSteps.map(step => ({
      ...step,
      arrows: Array.isArray(step.arrows) ? step.arrows : [],
      circles: step.circles && typeof step.circles === 'object' ? step.circles : {},
      ignitedLines: Array.isArray(step.ignitedLines) ? step.ignitedLines : savedLines,
    }));
    const first = savedSteps[0];

    setTitle(activity?.title || '');
    setSteps(savedSteps);
    setStepIndex(0);
    setStepText(first.explanation || '');
    setFen(first.fen || activity?.fen || INITIAL_FEN);

    if (startInPresentation) {
      setPresentationSnapshot({ steps: savedSteps, stepIndex: 0 });
      setIgnitedLines([]);
      setArrows([]);
      setCircles({});
      setPresentationMode(true);
    } else {
      setIgnitedLines(first.ignitedLines);
      setArrows(first.arrows);
      setCircles(first.circles);
      setPresentationSnapshot(null);
      setPresentationMode(false);
    }

    setLineType('rank');
    setLine('4');
  }, [open, activity, startInPresentation]);

  const getCurrentStep = () => ({
    ...(steps[stepIndex] || {}),
    title: steps[stepIndex]?.title || `Paso ${stepIndex + 1}`,
    explanation: stepText,
    fen,
    ignitedLines: [...ignitedLines],
    arrows: [...arrows],
    circles: { ...circles },
  });

  const loadStep = (step, index) => {
    setStepIndex(index);
    setStepText(step.explanation || '');
    setFen(step.fen || INITIAL_FEN);
    setIgnitedLines(Array.isArray(step.ignitedLines) ? step.ignitedLines : []);
    setArrows(Array.isArray(step.arrows) ? step.arrows : []);
    setCircles(step.circles && typeof step.circles === 'object' ? step.circles : {});
  };

  const goToStep = (index) => {
    const updated = [...steps];
    updated[stepIndex] = getCurrentStep();
    setSteps(updated);
    if (index >= 0 && index < updated.length) {
      loadStep(updated[index], index);
    }
  };

  const addStep = () => {
    const updated = [...steps];
    updated[stepIndex] = getCurrentStep();
    const next = {
      title: `Paso ${updated.length + 1}`,
      explanation: '',
      fen,
      ignitedLines: [...ignitedLines],
      arrows: [...arrows],
      circles: { ...circles },
    };
    updated.push(next);
    setSteps(updated);
    loadStep(next, updated.length - 1);
  };

  const deleteStep = () => {
    if (steps.length <= 1) {
      toast.error('La explicación debe tener al menos un paso');
      return;
    }
    // Guarda primero los cambios del paso actual antes de eliminarlo.
    const currentSteps = [...steps];
    currentSteps[stepIndex] = getCurrentStep();
    const updated = currentSteps.filter((_, index) => index !== stepIndex);
    const nextIndex = Math.min(stepIndex, updated.length - 1);
    setSteps(updated);
    loadStep(updated[nextIndex], nextIndex);
  };

  const saveActivity = async () => {
    if (!studyId) {
      toast.error('No se identificó el estudio donde guardar la actividad');
      return;
    }
    if (!title.trim()) {
      toast.error('Escribe el título de la actividad');
      return;
    }
    const payload = {
      type: 'lava_floor',
      title: title.trim(),
      points: Number(activity?.points ?? 50),
      max_attempts: Number(activity?.max_attempts ?? 3),
      time_seconds: Number(activity?.time_seconds ?? 120),
      // El FEN principal siempre corresponde a la primera escena, no a la última editada.
      fen: (steps.map((step, index) =>
        index === stepIndex ? getCurrentStep() : step
      )[0]?.fen) || fen,
      lava_config: {
        ignitedLines: (steps.map((step, index) =>
          index === stepIndex ? getCurrentStep() : step
        )[0]?.ignitedLines) || [],
        steps: steps.map((step, index) =>
          index === stepIndex ? getCurrentStep() : step
        ),
      },
      instructions: activity?.instructions || 'Llega a la casilla meta evitando todas las casillas con lava.'
    };

    setSaving(true);
    try {
      if (activity?.id) {
        await api.patch(`/studies/${studyId}/activities/${activity.id}`, payload);
      } else {
        await api.post(`/studies/${studyId}/activities`, payload);
      }
      toast.success(activity?.id ? 'Actividad de lava actualizada' : 'Actividad de lava guardada');
      if (onSaved) await onSaved();
      if (onClose) onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo guardar la actividad');
    } finally {
      setSaving(false);
    }
  };

  const squareStyles = useMemo(() => {
    const styles = {};

    ignitedLines.forEach(key => {
      const [type, value] = String(key).split(':');
      if (!['rank', 'file'].includes(type)) return;

      const isRank = type === 'rank';
      const squares = isRank
        ? [...FILES].map(file => `${file}${value}`)
        : Array.from({ length: 8 }, (_, i) => `${value}${i + 1}`);

      squares.forEach(square => {
        const fileIndex = FILES.indexOf(square[0]);
        const rankIndex = 8 - Number(square[1]);

        styles[square] = {
        backgroundColor: '#21120d',
        backgroundImage: isRank
          ? "url('/images/chess4kings-lava-texture.png')"
          : "url('/images/chess4kings-lava-texture-vertical.png')",
        backgroundSize: isRank ? '520px 65px' : '65px 520px',
        backgroundPosition: isRank
          ? `${-fileIndex * 65}px 0px`
          : `0px ${-rankIndex * 65}px`,
        backgroundRepeat: 'no-repeat',
        boxShadow: [
          'inset 0 0 0 2px rgba(255, 75, 8, 0.98)',
          'inset 0 0 9px rgba(255, 194, 45, 0.95)',
          'inset 0 0 20px rgba(255, 48, 5, 0.78)',
          '0 0 6px rgba(255, 80, 5, 0.5)',
        ].join(', '),
        };
      });
    });

    return styles;
  }, [ignitedLines]);

  const isIgnited = ignitedLines.includes(`${lineType}:${line}`);

  const handlePieceDrop = (from, to) => {
    try {
      const game = new Chess(fen);
      const move = game.move({ from, to });
      if (!move) return false;
      setFen(game.fen());
      return true;
    } catch {
      return false;
    }
  };

  const openPositionEditor = () => {
    setEditFen(fen || INITIAL_FEN);
    setEditTool(null);
    setPositionEditorOpen(true);
  };

  const updateEditSquare = (square, pieceCode) => {
    try {
      const game = new Chess(editFen || INITIAL_FEN, { skipValidation: true });
      game.remove(square);
      if (pieceCode) {
        const color = pieceCode[0] === 'w' ? 'w' : 'b';
        const type = pieceCode[1].toLowerCase();
        game.put({ color, type }, square);
      }
      setEditFen(game.fen());
    } catch {
      toast.error('No se pudo modificar esa casilla');
    }
  };

  const applyEditedPosition = () => {
    try {
      const parts = (editFen || INITIAL_FEN).trim().split(/\s+/);
      const normalizedFen = `${parts[0]} ${parts[1] === 'b' ? 'b' : 'w'} - - 0 1`;
      new Chess(normalizedFen, { skipValidation: true });
      setFen(normalizedFen);
      setPositionEditorOpen(false);
      toast.success('Posición aplicada al paso actual');
    } catch {
      toast.error('No se pudo aplicar la posición');
    }
  };

  const toggleLine = (type, value) => {
    const key = `${type}:${value}`;
    setIgnitedLines(current =>
      current.includes(key)
        ? current.filter(item => item !== key)
        : [...current, key]
    );
  };

  const toggleLava = () => toggleLine(lineType, line);

  const startPresentation = () => {
    const updated = [...steps];
    const currentStep = getCurrentStep();
    updated[stepIndex] = currentStep;

    // Conserva el estado de preparación para restaurarlo al salir.
    setPresentationSnapshot({
      steps: updated,
      stepIndex,
    });
    setSteps(updated);

    // La explicación comienza sin lava ni anotaciones temporales.
    setFen(currentStep.fen || INITIAL_FEN);
    setStepText(currentStep.explanation || '');
    setIgnitedLines([]);
    setArrows([]);
    setCircles({});
    setPresentationMode(true);
  };

  const goToPresentationStep = (index) => {
    if (index < 0 || index >= steps.length) return;

    const step = steps[index];
    setStepIndex(index);
    setStepText(step.explanation || '');
    setFen(step.fen || INITIAL_FEN);

    // Cada paso antiguo inicia limpio durante la explicación.
    setIgnitedLines([]);
    setArrows([]);
    setCircles({});
  };

  const lavaActivityIndex = lavaActivities.findIndex(item => item.id === activity?.id);
  const hasMultipleLavaActivities = lavaActivities.length > 1;

  const navigatePresentation = (direction) => {
    if (hasMultipleLavaActivities && lavaActivityIndex >= 0) {
      const nextIndex = lavaActivityIndex + direction;
      if (nextIndex >= 0 && nextIndex < lavaActivities.length) {
        if (onNavigateActivity) onNavigateActivity(lavaActivities[nextIndex]);
      }
      return;
    }

    goToPresentationStep(stepIndex + direction);
  };

  const exitPresentation = () => {
    if (presentationSnapshot) {
      setSteps(presentationSnapshot.steps);
      const restoredIndex = presentationSnapshot.stepIndex;
      const restoredStep = presentationSnapshot.steps[restoredIndex];
      if (restoredStep) loadStep(restoredStep, restoredIndex);
    }
    setPresentationSnapshot(null);
    setPresentationMode(false);
  };

  return (
    <div className={open ? 'block' : 'hidden'}>
      {!presentationMode && (
      <div className="flex flex-col gap-4 rounded-xl border border-orange-200 bg-orange-50/40 p-3 sm:p-5">
        <aside className="mb-2 flex flex-col gap-2 rounded-xl border border-orange-200 bg-orange-50 p-2 sm:flex-row sm:items-center sm:gap-3 sm:p-3">
          <div className="flex shrink-0 items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-orange-100 text-orange-700">
              <Flame className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-orange-950">El piso es lava</h3>
              <p className="text-xs text-orange-800">Datos de la actividad</p>
            </div>
          </div>

          <div className="min-w-0 flex-1 space-y-2">
            <label htmlFor="lava-title" className="text-xs font-medium text-slate-700">
              Título de la actividad
            </label>
            <input
              id="lava-title"
              value={title}
              onChange={event => setTitle(event.target.value)}
              maxLength={120}
              placeholder="Ej. Mate con dos torres"
              className="w-full rounded-md border border-orange-200 bg-white px-3 py-2 text-sm focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100"
            />
          </div>

          <Button
            type="button"
            className="h-8 shrink-0 bg-orange-600 px-3 text-xs text-white hover:bg-orange-700"
            onClick={saveActivity}
            disabled={saving}
          >
            {saving ? 'Guardando...' : activity?.id ? 'Guardar cambios' : 'Guardar actividad'}
          </Button>

        </aside>

        <section className="min-w-0 rounded-xl border border-orange-200 bg-orange-50/50 p-2 sm:p-3">
          <div className="flex flex-col items-center gap-4 lg:flex-row lg:items-center lg:gap-6">
            <div className="flex min-w-0 flex-1 justify-center">
              <ChessBoardView
                fen={fen}
                onPieceDrop={handlePieceDrop}
                boardWidth={380}
                arePiecesDraggable={true}
                extraSquareStyles={squareStyles}
              />
            </div>
            <div className="flex w-full flex-wrap justify-center gap-2 lg:w-44 lg:shrink-0 lg:flex-col lg:items-stretch">
            <Button type="button" className="h-8 bg-orange-600 px-3 text-xs text-white hover:bg-orange-700" onClick={startPresentation}>
              Iniciar actividad
            </Button>
            <Button type="button" variant="outline" className="h-8 border-orange-300 px-3 text-xs text-orange-900 hover:bg-orange-100" onClick={openPositionEditor}>
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              Editar tablero
            </Button>
            <Button type="button" variant="outline" className="h-8 border-orange-300 px-3 text-xs text-orange-900 hover:bg-orange-100" onClick={onClose}>
              Cerrar editor
            </Button>
            </div>
          </div>
        </section>


      </div>
      )}

      {presentationMode && (
        <section className="space-y-4 rounded-xl border bg-slate-50 p-3 sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-bold text-slate-900">
                {title || 'El piso es lava'}
              </h3>
              <p className="text-xs text-slate-600">
                Explicación del profesor · Turno:
                {' '}{(fen.split(' ')[1] || 'w') === 'w' ? 'blancas' : 'negras'}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={exitPresentation}
            >
              Volver a preparación
            </Button>
          </div>

          <div className="grid min-w-0 grid-cols-1 items-center gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(420px,500px)]">
            <div
              ref={presentationBoardAreaRef}
              className="flex min-h-[min(72vh,680px)] w-full min-w-0 items-center justify-center overflow-x-auto rounded-xl border border-orange-100 bg-white/70 p-2"
            >
              <div className="mx-auto w-fit max-w-full">
                <ChessBoardView
                  fen={fen}
                  onPieceDrop={handlePieceDrop}
                  boardWidth={presentationBoardWidth}
              arePiecesDraggable={true}
              allowAnnotations={true}
              clearAnnotationsOnLeftClick={true}
              arrows={arrows}
              circles={circles}
              onAnnotationsChange={(nextArrows, nextCircles) => {
                setArrows(nextArrows || []);
                setCircles(nextCircles || {});
              }}
                  extraSquareStyles={squareStyles}
                />
              </div>
            </div>

            <div className="min-w-0 space-y-4 xl:max-h-[calc(100vh-190px)] xl:overflow-y-auto xl:pr-1">
              <div className="rounded-lg border border-orange-100 bg-white p-3">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h4 className="font-semibold text-slate-900">
                {steps[stepIndex]?.title || `Paso ${stepIndex + 1}`}
              </h4>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={
                    hasMultipleLavaActivities
                      ? lavaActivityIndex <= 0
                      : stepIndex === 0
                  }
                  onClick={() => navigatePresentation(-1)}
                >
                  Anterior
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={
                    hasMultipleLavaActivities
                      ? lavaActivityIndex < 0 || lavaActivityIndex >= lavaActivities.length - 1
                      : stepIndex >= steps.length - 1
                  }
                  onClick={() => navigatePresentation(1)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
            {stepText && (
              <p className="whitespace-pre-wrap text-sm text-slate-700">
                {stepText}
              </p>
            )}
            <p className="mt-2 text-xs text-slate-500">
              {hasMultipleLavaActivities
                ? `Posición ${lavaActivityIndex + 1} de ${lavaActivities.length} del estudio.`
                : `Paso ${stepIndex + 1} de ${steps.length}.`}
              {' '}Los movimientos respetan las reglas y el turno del ajedrez.
            </p>
          </div>

              <div className="space-y-2.5 rounded-lg border border-orange-200 bg-white p-2.5">
            <div className="flex items-center gap-2 font-semibold text-orange-900">
              <Flame className="h-5 w-5" />
              Control temporal de lava
            </div>
            <div className="space-y-3">
              <div className="space-y-1">
                <p className="text-xs font-semibold text-orange-900">Filas</p>
                <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-8">
                  {['1', '2', '3', '4', '5', '6', '7', '8'].map(value => {
                    const active = ignitedLines.includes(`rank:${value}`);
                    return (
                      <button
                        key={`rank-${value}`}
                        type="button"
                        aria-pressed={active}
                        onClick={() => toggleLine('rank', value)}
                        className={`rounded-lg border px-1 py-1.5 text-[11px] font-semibold whitespace-nowrap transition-colors ${
                          active
                            ? 'border-orange-500 bg-orange-100 text-orange-900 shadow-inner'
                            : 'border-slate-200 bg-white text-slate-700 hover:border-orange-300 hover:bg-orange-50'
                        }`}
                      >
                        <Flame className={`mx-auto mb-0.5 h-3.5 w-3.5 ${active ? 'text-orange-600' : 'text-slate-400'}`} />
                        Fila {value}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-orange-900">Columnas</p>
                <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-8">
                  {[...FILES].map(value => {
                    const active = ignitedLines.includes(`file:${value}`);
                    return (
                      <button
                        key={`file-${value}`}
                        type="button"
                        aria-pressed={active}
                        onClick={() => toggleLine('file', value)}
                        className={`rounded-lg border px-1 py-1.5 text-[11px] font-semibold whitespace-nowrap transition-colors ${
                          active
                            ? 'border-orange-500 bg-orange-100 text-orange-900 shadow-inner'
                            : 'border-slate-200 bg-white text-slate-700 hover:border-orange-300 hover:bg-orange-50'
                        }`}
                      >
                        <Flame className={`mx-auto mb-0.5 h-3.5 w-3.5 ${active ? 'text-orange-600' : 'text-slate-400'}`} />
                        Col. {value}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-orange-200 bg-orange-50 p-2.5 text-xs font-medium text-orange-950">
              <input
                type="checkbox"
                checked={isIgnited}
                onChange={toggleLava}
                className="h-4 w-4 accent-orange-600"
              />
              <Flame className="h-4 w-4" />
              {isIgnited ? 'Apagar' : 'Encender'} {lineType === 'rank' ? `fila ${line}` : `columna ${line}`}
            </label>
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
              <span>{ignitedLines.length} línea(s) encendida(s)</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIgnitedLines([])}
                disabled={ignitedLines.length === 0}
              >
                Apagar toda la lava
              </Button>
            </div>
                <p className="text-xs text-slate-500">
                  Estos controles solo afectan la explicación actual; no modifican las posiciones guardadas.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {positionEditorOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-3">
          <div className="max-h-[94vh] w-full max-w-4xl overflow-y-auto rounded-xl bg-white p-4 shadow-xl">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold">Editar posición del tablero</h3>
                <p className="text-sm text-slate-500">
                  Selecciona una pieza y pulsa una casilla para colocarla.
                </p>
              </div>
              <Button type="button" variant="outline" onClick={() => setPositionEditorOpen(false)}>
                Cancelar
              </Button>
            </div>

            <div className="grid gap-5 md:grid-cols-[minmax(0,360px)_1fr]">
              <div className="mx-auto w-full max-w-[360px]">
                <ChessBoardView
                  fen={editFen}
                  boardWidth={360}
                  arePiecesDraggable={true}
                  onPieceDrop={(from, to) => {
                    try {
                      const game = new Chess(editFen, { skipValidation: true });
                      const piece = game.remove(from);
                      if (!piece) return false;
                      game.remove(to);
                      if (editTool !== 'erase') game.put(piece, to);
                      setEditFen(game.fen());
                      return true;
                    } catch {
                      return false;
                    }
                  }}
                  onSquareClick={(square) => {
                    if (!editTool) return;
                    updateEditSquare(square, editTool === 'erase' ? null : editTool);
                  }}
                />
              </div>

              <div className="space-y-4">
                <div>
                  <p className="mb-2 text-sm font-semibold">Piezas</p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      ['wK','♔'],['wQ','♕'],['wR','♖'],
                      ['wB','♗'],['wN','♘'],['wP','♙'],
                      ['bK','♚'],['bQ','♛'],['bR','♜'],
                      ['bB','♝'],['bN','♞'],['bP','♟'],
                    ].map(([piece, symbol]) => (
                      <Button
                        key={piece}
                        type="button"
                        size="sm"
                        variant={editTool === piece ? 'default' : 'outline'}
                        className="h-11 text-2xl"
                        onClick={() => setEditTool(piece)}
                      >
                        {symbol}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant={editTool === 'erase' ? 'default' : 'outline'}
                    onClick={() => setEditTool('erase')}
                  >
                    <Eraser className="mr-2 h-4 w-4" /> Borrar pieza
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setEditFen(INITIAL_FEN); setEditTool(null); }}
                  >
                    Posición inicial
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setEditFen('8/8/8/8/8/8/8/8 w - - 0 1');
                      setEditTool(null);
                    }}
                  >
                    Tablero vacío
                  </Button>
                </div>

                <div>
                  <p className="mb-1 text-sm font-medium">Turno</p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={(editFen.split(' ')[1] || 'w') === 'w' ? 'default' : 'outline'}
                      onClick={() => {
                        const parts = editFen.split(' ');
                        parts[1] = 'w';
                        setEditFen(parts.join(' '));
                      }}
                    >Blancas</Button>
                    <Button
                      type="button"
                      variant={editFen.split(' ')[1] === 'b' ? 'default' : 'outline'}
                      onClick={() => {
                        const parts = editFen.split(' ');
                        parts[1] = 'b';
                        setEditFen(parts.join(' '));
                      }}
                    >Negras</Button>
                  </div>
                </div>

                <details>
                  <summary className="cursor-pointer text-sm font-medium">FEN avanzado</summary>
                  <textarea
                    rows={3}
                    value={editFen}
                    onChange={event => setEditFen(event.target.value)}
                    className="mt-2 w-full rounded-md border p-2 font-mono text-xs"
                  />
                </details>

                <Button type="button" className="w-full" onClick={applyEditedPosition}>
                  Aplicar posición
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
